import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Leaf, ShoppingBag, Minus, Plus, Truck, Tag, FlaskConical, Gift, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ProductSpecsBlock from '../components/ProductSpecsBlock';
import ReviewsAndRatings from '../components/ReviewsAndRatings';
import ProductFAQ from '../components/ProductFAQ';
import SEO from '../components/SEO';
import { WhyThisPrice, ObjectionFAQ, ReturnsTrustBadge } from '../components/PricingRationale';
import ClinicalResearchSection from '../components/ClinicalResearchSection';
import ImageLightbox from '../components/ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  // Hide the first-time bumper card once the customer has actually paid
  // on this device (PaymentSuccess sets `iiy_has_purchased`).
  const [hasPurchasedFlag, setHasPurchasedFlag] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try { setHasPurchasedFlag(!!localStorage.getItem('iiy_has_purchased')); } catch (e) { /* private mode */ }
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // Auto-clear coupon if quantity moves outside the coupon's allowed range.
  // MAY15 (applies_to=single_bottle_only) clears at qty != 1; EMPYEE50 (applies_to=all) stays.
  useEffect(() => {
    if (!appliedCoupon) return;
    if (appliedCoupon.applies_to === 'single_bottle_only' && quantity !== 1) {
      const code = appliedCoupon.code;
      setAppliedCoupon(null);
      setCouponError('');
      toast.info(`${code} removed - applies only to single-bottle orders.`);
      return;
    }
    // For all-quantity coupons, re-validate so saved_amount tracks the new quantity
    if (appliedCoupon.applies_to === 'all') {
      axios.post(`${API}/coupons/validate`, { code: appliedCoupon.code, quantity })
        .then(r => { if (r.data?.valid) setAppliedCoupon(r.data); })
        .catch(() => {});
    }
  }, [quantity]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a partner code is removed, restore the 3-bottle retail cap.
  useEffect(() => {
    const cap = appliedCoupon?.type === 'fixed_price_per_bottle' ? (Number(appliedCoupon.max_qty) || 300) : 3;
    setQuantity((cur) => Math.min(cur, cap));
  }, [appliedCoupon]);

  const handleApplyCoupon = async () => {
    setCouponError('');
    const code = (couponInput || '').trim().toUpperCase();
    if (!code) { setCouponError('Enter a coupon code.'); return; }
    try {
      const r = await axios.post(`${API}/coupons/validate`, { code, quantity });
      if (r.data?.valid) {
        setAppliedCoupon(r.data);
        toast.success(`Coupon ${r.data.code} applied - ${r.data.value_pct}% off!`);
      } else {
        setCouponError(r.data?.reason || 'Invalid coupon.');
      }
    } catch (e) {
      setCouponError('Could not validate coupon. Try again.');
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO
          title="Buy BCM-95® Curcumin 650mg UK | High-Bioavailability | IIY"
          description="Buy authentic BCM-95® in the UK from IIY Phytoventra. 95% curcuminoid complex with up to 8-hour absorption, 7× more bioavailable. Vegan, allergen-free, free Royal Mail delivery."
        />
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary"></div>
      </div>
    );
  }

  if (!product) return null;

  const allImages = [product.image, ...(product.images || [])];
  const altFor = (i) => `${product.name} — product photo ${i + 1}`;
  // Bumper-offer pricing (2026), gross/all-inclusive of UK VAT + standard
  // delivery. 1/2/3-bottle totals; 4+ bottles bill at the flat £31/bottle rate.
  const PRICES = { 1: 37.49, 2: 70.00, 3: 93.00 };
  const ABOVE_RATE = 31.00;              // per bottle for qty > 3
  const REF_PB = { 1: 44.99, 2: 42.49, 3: 38.00 };  // pre-offer, for "you save"
  const REF_ABOVE = 38.00;
  const RETAIL_MAX = 300;
  const QTY_LABEL = { 1: 'Single Bottle', 2: '2 Bottles', 3: '3 Bottles · Best Value' };
  const tierTotal = (n) => (PRICES[n] != null ? PRICES[n] : +(ABOVE_RATE * n).toFixed(2));
  const isFixedCoupon = appliedCoupon?.type === 'fixed_price_per_bottle';
  const couponPerBottle = isFixedCoupon ? Number(appliedCoupon.price_per_bottle ?? appliedCoupon.value ?? 0) : null;
  const maxQty = isFixedCoupon ? (Number(appliedCoupon.max_qty) || 300) : RETAIL_MAX;
  const q = Math.max(1, Math.min(quantity, maxQty));
  const perBottle = isFixedCoupon ? couponPerBottle : +((tierTotal(q)) / q).toFixed(2);
  const tier = { price: perBottle, label: isFixedCoupon ? appliedCoupon.code : (QTY_LABEL[q] || `${q} Bottles`) };
  let gross = isFixedCoupon ? +(couponPerBottle * q).toFixed(2) : tierTotal(q);
  // "You save" amount (no percentage shown), vs the pre-offer reference price.
  const refPb = REF_PB[q] != null ? REF_PB[q] : REF_ABOVE;
  const saveAmount = isFixedCoupon ? 0 : Math.max(0, +((refPb * q) - gross).toFixed(2));
  let couponSavings = 0;
  const autoApplied15 = false;
  if (appliedCoupon && gross !== null && (isFixedCoupon || appliedCoupon.applies_to === 'all')) {
    couponSavings = +(appliedCoupon.saved_amount || 0).toFixed(2);
    if (!isFixedCoupon && couponSavings) {
      gross = +(gross - couponSavings).toFixed(2);
    }
  }
  const grossStr = gross.toFixed(2);
  const total = grossStr;

  const handlePreOrder = () => {
    navigate('/checkout', { state: { productId: id, quantity: q, product, couponCode: appliedCoupon?.code || '' } });
  };

  const isWholesale = false;

  return (
    <div className="min-h-screen py-24 md:py-32">
      <SEO
        title="Buy BCM-95® Curcumin 650mg UK | High-Bioavailability | IIY"
        description="Buy authentic BCM-95® in the UK from IIY Phytoventra. 95% curcuminoid complex with up to 8-hour absorption, 7× more bioavailable. Vegan, allergen-free, free Royal Mail delivery."
      />
      {/* BreadcrumbList JSON-LD — surfaces site hierarchy in Google search */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://iiypv.com/' },
              { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://iiypv.com/products' },
              { '@type': 'ListItem', position: 3, name: product?.name || 'BCM-95®', item: `https://iiypv.com/products/${id}` },
            ],
          }),
        }}
      />
      {/* HowTo JSON-LD — surfaces "How to take BCM-95®" as a Google rich-result.
          Wording strictly MHRA/ASA-compliant — no medical or curative claims. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to take BCM-95® curcumin food supplement',
            description:
              'A simple daily routine to incorporate BCM-95® into your active-living regimen. Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle.',
            image: 'https://iiypv.com/bcm95-bottle.jpg',
            totalTime: 'PT2M',
            supply: [
              { '@type': 'HowToSupply', name: 'One bottle of BCM-95® — 60 vegetarian capsules' },
              { '@type': 'HowToSupply', name: 'A glass of water (approx. 200 ml)' },
            ],
            tool: [{ '@type': 'HowToTool', name: 'A regular meal' }],
            step: [
              {
                '@type': 'HowToStep',
                name: 'Choose a consistent time',
                text: 'Pick a meal you eat daily — breakfast or your main evening meal works best. Consistency helps you build the habit and means the supplement is taken alongside dietary fats, which support absorption.',
                url: 'https://iiypv.com/products/94056720-f372-4eef-bcfe-74063137a6e5#step-1',
              },
              {
                '@type': 'HowToStep',
                name: 'Take 1–2 vegetarian capsules with food',
                text: 'Swallow one or two BCM-95® capsules with a glass of water alongside your chosen meal. Each capsule contains 500 mg of Purified Turmeric Extract Curcugreen®; two capsules provide 1000 mg of extract, of which 650 mg is curcumin.',
                url: 'https://iiypv.com/products/94056720-f372-4eef-bcfe-74063137a6e5#step-2',
              },
              {
                '@type': 'HowToStep',
                name: 'Repeat daily',
                text: 'Take consistently every day as part of an active-living routine. Bioavailable curcumin supports joint comfort and everyday well-being when used as part of a balanced diet.',
                url: 'https://iiypv.com/products/94056720-f372-4eef-bcfe-74063137a6e5#step-3',
              },
              {
                '@type': 'HowToStep',
                name: 'Store correctly between uses',
                text: 'Keep the bottle tightly closed and store in a cool, dry place between 15°C and 30°C, out of direct sunlight and out of reach of children.',
                url: 'https://iiypv.com/products/94056720-f372-4eef-bcfe-74063137a6e5#step-4',
              },
            ],
          }),
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Products
        </button>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:sticky lg:top-24 self-start">
            <div className="bg-gradient-to-br from-primary/10 to-white rounded-3xl p-6 mb-4">
              <img
                src={allImages[selectedImage]}
                alt={altFor(selectedImage)}
                onClick={() => setLightboxOpen(true)}
                className="w-full rounded-2xl cursor-zoom-in"
                data-testid="product-main-image"
              />
            </div>
            {allImages.length > 1 && (
              <div className="grid grid-cols-3 gap-2">
                {allImages.map((image, index) => (
                  <button
                    key={image}
                    onClick={() => setSelectedImage(index)}
                    className={`bg-white rounded-xl p-1.5 border-2 transition-all aspect-square overflow-hidden ${
                      selectedImage === index ? 'border-primary shadow-lg' : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`product-thumb-${index}`}
                  >
                    <img loading="lazy" decoding="async" src={image} alt={altFor(index)} className="w-full h-full object-cover rounded-lg" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
          <ImageLightbox open={lightboxOpen} images={allImages} index={selectedImage} altFor={altFor} onClose={() => setLightboxOpen(false)} onIndexChange={setSelectedImage} />

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-primary mb-2 block">
                Patented BCM-95® Curcumin
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading" data-testid="product-detail-title">
                {product.name}
              </h1>
              {/* Authenticity badge — high-intent search visitors look for this validator */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-700/25 text-emerald-900 text-xs font-bold mt-1 mb-3" data-testid="authentic-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 12l2 2 4-4" />
                  <path d="M12 2l3 3 4-1 1 4 3 3-3 3-1 4-4-1-3 3-3-3-4 1-1-4-3-3 3-3 1-4 4 1z" />
                </svg>
                Authentic Patented BCM-95<sup>&reg;</sup> Formulation
              </div>
              <p className="text-sm text-muted-foreground">(60 Capsules per Bottle · Made in UK)</p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-3">
                {product.description}
              </p>
            </div>

            {/* Pricing Section */}
            <div className="bg-white border-2 border-primary/20 rounded-2xl p-6" data-testid="pricing-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  {isWholesale ? (
                    <>
                      <p className="text-2xl font-bold text-amber-700">Custom Quote Required</p>
                      <p className="text-sm text-muted-foreground mt-1">Wholesale pricing for 301+ bottles (min £25/bottle all-inclusive of VAT)</p>
                    </>
                  ) : (
                    <>
                      <p className="text-3xl font-bold text-secondary">£{tier.price.toFixed(2)} <span className="text-base font-normal text-muted-foreground">per bottle</span></p>
                      {tier.discount > 0 && (
                        <p className="text-sm text-primary font-semibold mt-1">
                          <Tag className="w-4 h-4 inline mr-1" />{tier.label}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium">Quantity:</span>
                <div className="flex items-center border-2 border-border rounded-xl overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 hover:bg-gray-100 transition-colors"
                    data-testid="qty-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={maxQty}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(maxQty, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-20 text-center py-2 font-semibold border-x-2 border-border focus:outline-none"
                    data-testid="qty-input"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(maxQty, quantity + 1))}
                    className="px-4 py-2 hover:bg-gray-100 transition-colors"
                    data-testid="qty-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Product spec line — always visible next to price */}
              <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-600" data-testid="product-spec-line">
                <span>• 500mg BCM-95<sup>&reg;</sup> per capsule</span>
                <span>• 60 vegan capsules</span>
                <span>• 30-day supply per bottle</span>
              </div>

              {/* Price Breakdown */}
              {!isWholesale && (
              <div className="border-t border-border pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>{q} × £{tier?.price.toFixed(2)}</span>
                  <span>£{tier ? (tier.price * q).toFixed(2) : '—'}</span>
                </div>
                {!appliedCoupon && saveAmount > 0 && (
                  <div className="flex justify-between text-emerald-700 font-semibold" data-testid="you-save-row">
                    <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> You save</span>
                    <span>-£{saveAmount.toFixed(2)}</span>
                  </div>
                )}
                {appliedCoupon && couponSavings > 0 && (
                  <div className="flex justify-between text-amber-700 font-medium" data-testid="coupon-savings-row">
                    <span>Coupon {appliedCoupon.code}</span>
                    <span>-£{couponSavings.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                  <span>Total</span>
                  <span>£{total}</span>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug pt-1">
                  Price all-inclusive of UK VAT (20%) and standard delivery.
                </p>
              </div>
              )}

              {/* First-Time Buyer Bumper Offer — visible re-entry point for qty=1.
                  Hidden once the customer has purchased on this device (via
                  the iiy_has_purchased localStorage flag set by PaymentSuccess). */}
              {false && quantity === 1 && !hasPurchasedFlag && (
                <div
                  className="mt-4 relative overflow-hidden rounded-2xl border border-amber-400/60 bg-gradient-to-br from-amber-50 via-amber-100/70 to-yellow-50 p-5"
                  data-testid="first-time-bumper-card"
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-300/30 rounded-full blur-2xl pointer-events-none" />
                  <div className="relative flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-600 text-white flex items-center justify-center shadow-md flex-shrink-0">
                      <Gift className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-1">
                        First-Time Buyer Exclusive
                      </div>
                      <div className="text-base font-bold text-amber-900 mb-1">
                        Try BCM-95® for just <span className="text-xl">£37.99</span>
                      </div>
                      <p className="text-[12px] text-amber-900/80 leading-snug mb-2">
                        One-time welcome price on a single bottle &mdash; all-inclusive of UK VAT &amp; FREE Royal Mail 48 delivery. No code needed.
                      </p>
                      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-3" data-testid="first-time-bumper-deadline">
                        Offer ends 30 June 2026
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/bumper-offer?source=pd&firsttime=1')}
                        className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-bold px-4 py-2 rounded-lg shadow transition-colors"
                        data-testid="first-time-bumper-claim"
                      >
                        Claim My Welcome Offer
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Discreet "Have a code?" entry — partner / staff codes (e.g. EMPYEE50).
                  Available on every quantity; never exposes EMPYEE50 publicly. */}
              {!isWholesale && !appliedCoupon && (
                <div className="mt-4" data-testid="have-code-area">
                  {!showCodeInput ? (
                    <button
                      type="button"
                      onClick={() => setShowCodeInput(true)}
                      className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2 transition-colors"
                      data-testid="have-code-toggle"
                    >Have a code?</button>
                  ) : (
                    <div className="border border-border rounded-lg p-3 bg-muted/30">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="flex-1 px-3 py-2 rounded-lg border border-border focus:outline-none focus:border-primary text-sm font-mono"
                          data-testid="hidden-coupon-input"
                        />
                        <button
                          onClick={handleApplyCoupon}
                          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                          data-testid="hidden-coupon-apply"
                        >Apply</button>
                      </div>
                      {couponError && <p className="text-xs text-red-700 mt-2" data-testid="hidden-coupon-error">{couponError}</p>}
                    </div>
                  )}
                </div>
              )}

              {/* When an 'all quantities' coupon (e.g. EMPYEE50) is applied at qty != 1, show a compact applied chip */}
              {!isWholesale && quantity !== 1 && appliedCoupon && (
                <div className="mt-4 bg-primary/5 border border-primary/30 rounded-lg p-3 flex items-center justify-between" data-testid="coupon-applied-chip">
                  <div className="text-sm">
                    <span className="font-semibold text-primary">Code {appliedCoupon.code} applied</span>
                    {couponSavings > 0 && (
                      <span className="text-muted-foreground"> &middot; You saved £{couponSavings.toFixed(2)}</span>
                    )}
                  </div>
                  <button
                    onClick={() => { setAppliedCoupon(null); setCouponInput(''); }}
                    className="text-xs text-muted-foreground hover:text-primary underline"
                    data-testid="coupon-remove-chip"
                  >Remove</button>
                </div>
              )}
            </div>

            {/* 14-Day UK Returns trust badge — surfaces the consumer-protection
                trust signal alongside the buy area (audit-flagged gap). */}
            <ReturnsTrustBadge />

            {/* Wholesale enquiry (shop capped at 3 bottles) */}
            <div className="bg-gradient-to-br from-primary/5 to-white rounded-2xl p-5 text-sm" data-testid="wholesale-note">
              <p className="text-muted-foreground">
                Need more than 300 bottles? <button onClick={() => navigate('/contact')} className="text-primary font-semibold underline underline-offset-2" data-testid="wholesale-enquiry-link">Contact us for wholesale pricing</button>. All prices are all-inclusive of UK VAT (20%) and FREE UK delivery.
              </p>
            </div>

            {/* Delivery Info */}
            <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4">
              <Truck className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900">Free UK Delivery</p>
                <p className="text-blue-700">Standard delivery - 2-3 working days (FREE)</p>
              </div>
            </div>

            {/* Buy Now — prominent primary CTA */}
            <button
              onClick={handlePreOrder}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-lg md:text-xl py-5 px-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-3 shadow-2xl hover:shadow-amber-500/40 ring-2 ring-amber-500/20"
              data-testid="buy-now-button"
            >
              <ShoppingBag className="w-6 h-6" />
              {`Buy Now — £${total}`}
            </button>

            {/* Subscribe & Save 16.67% - only for the active, purchasable BCM-95® product */}
            {!isWholesale && product.available_for_purchase !== false && product.status !== 'coming_soon' && (
              <button
                onClick={() => navigate('/subscribe')}
                className="w-full bg-white border-2 border-primary text-primary hover:bg-primary/5 font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 group"
                data-testid="subscribe-cta-button"
              >
                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded">Save 31.10%</span>
                Subscribe &amp; Auto-Ship &mdash; £31.00/mo
                <span className="opacity-60 group-hover:opacity-100 transition-opacity">&rarr;</span>
              </button>
            )}
          </motion.div>
        </div>

        {/* ============ Product Specifications & Key Benefits ============ */}
        <div className="mt-12" data-testid="product-detail-specs">
          <ProductSpecsBlock />
        </div>

        {/* ============ "Why £44.99?" pricing-rationale strip (audit-flagged) ============ */}
        <WhyThisPrice />

        {/* ============ Clinical Research Evidence Base ============ */}
        <ClinicalResearchSection />

        {/* ============ Expert Endorsements & AggregateRating ============ */}
        <ReviewsAndRatings />

        {/* ============ Honest objection-handling FAQ (audit-flagged) ============ */}
        <ObjectionFAQ />

        {/* ============ Product FAQ (Google "People Also Ask") ============ */}
        <ProductFAQ />

        {/* ============ Full-width info grid (Features / Benefits / Ingredients) ============ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-16 grid md:grid-cols-3 gap-6"
          data-testid="product-info-grid"
        >
          {product.features && product.features.length > 0 && (
            <div className="bg-white border border-border/60 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 font-heading flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" /> Key Features
              </h3>
              <div className="space-y-2.5">
                {product.features.map((feature, index) => (
                  <div key={feature} className="flex items-start gap-2" data-testid={`feature-${index}`}>
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.benefits && product.benefits.length > 0 && (
            <div className="bg-gradient-to-br from-primary/5 via-white to-white border border-primary/15 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-4 font-heading flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" /> Health Benefits
              </h3>
              <div className="space-y-3">
                {product.benefits.map((benefit, index) => {
                  const parts = benefit.split(' - ');
                  return (
                    <div key={benefit} data-testid={`benefit-${index}`}>
                      <h4 className="font-semibold text-secondary text-sm mb-0.5">{parts[0]}</h4>
                      {parts[1] && <p className="text-xs text-muted-foreground leading-relaxed">{parts[1]}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {product.ingredients && (
            <div className="bg-white border-2 border-primary/20 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-3 font-heading flex items-center gap-2 text-primary">
                <FlaskConical className="w-5 h-5" /> Ingredients
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{product.ingredients}</p>
              <div className="mt-4 pt-4 border-t border-primary/10">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1">Recommended Usage</p>
                <p className="text-sm text-foreground"><strong>2 capsules per day</strong> &middot; one in the morning, one in the evening, after food.</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* ============ Why It Works ============ */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 mb-8"
          data-testid="why-it-works-section"
        >
          <div className="text-center mb-10">
            <span className="inline-block bg-amber-100/70 text-amber-900 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3">
              Simple Daily Routine
            </span>
            <h2 className="text-4xl md:text-5xl font-bold font-heading">
              Just Two Capsules <span className="text-primary">a Day.</span>
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Two capsules daily provide consistent BCM-95® intake, making BCM-95® easy to build into your everyday routine.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-primary/5 to-white border border-primary/10 rounded-2xl p-6 hover:shadow-xl transition-shadow" data-testid="product-pillar-1">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-lg">8h</span>
              </div>
              <h3 className="font-bold text-lg mb-2">8-Hour Active Support</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Most supplements pass through you in an hour. BCM-95® is engineered to stay <strong>active in your body for up to 8 hours</strong> &mdash; take one in the morning and one in the evening for round-the-clock support.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-300/30 rounded-2xl p-6 hover:shadow-xl transition-shadow" data-testid="product-pillar-2">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="font-bold text-lg mb-2">100% Natural &amp; Vegan</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We use <strong>Curcugreen<sup>&reg;</sup></strong> &mdash; the world's most-researched turmeric extract, with <strong>75+ peer-reviewed clinical studies</strong> and zero synthetic additives. Vegan-friendly capsules.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-300/30 rounded-2xl p-6 hover:shadow-xl transition-shadow" data-testid="product-pillar-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-700" />
              </div>
              <h3 className="font-bold text-lg mb-2">GMP-Certified Manufacturing</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Amber-glass bottle to protect potency from light. <strong>GMP-certified</strong> manufacturing standards. Patented BCM-95<sup>&reg;</sup> technology &mdash; <strong>7&times; more bioavailable</strong> than ordinary curcumin.
              </p>
            </div>
          </div>
        </motion.section>

        {/* ============ Final Purchase Reinforcement ============ */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mt-20 mb-8"
          data-testid="final-reinforcement"
        >
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-emerald-700 to-emerald-900 text-white rounded-3xl p-8 md:p-12 shadow-xl">
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4 leading-tight">
              Keep moving. Keep exploring. Keep enjoying life.
            </h2>
            <p className="text-emerald-50/90 max-w-2xl mx-auto mb-7">
              BCM-95® pairs clinically researched BCM-95® with a simple daily routine — an easy way to look after yourself while you get on with the things you love.
            </p>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-white text-emerald-900 font-bold py-3.5 px-8 rounded-full hover:bg-emerald-50 transition-colors shadow-lg hover:-translate-y-0.5"
              data-testid="final-reinforcement-cta"
            >
              Add BCM-95® to Your Daily Routine <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.section>

        {/* ============ UK statutory food-supplement disclaimer (every product page) ============ */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mt-16 mb-8"
          data-testid="statutory-disclaimer"
        >
          <div className="max-w-4xl mx-auto bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8">
            <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-slate-500 mb-3">
              Important Information
            </p>
            <ul className="space-y-2.5 text-sm text-slate-600 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-slate-400 flex-shrink-0">&bull;</span>
                <span>
                  Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400 flex-shrink-0">&bull;</span>
                <span>
                  If you are pregnant, breastfeeding, taking any medications or under medical supervision, please consult a doctor or healthcare professional before use.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400 flex-shrink-0">&bull;</span>
                <span>Keep out of reach of children.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400 flex-shrink-0">&bull;</span>
                <span>
                  This product is a food supplement and is not intended to diagnose, treat, cure, or prevent any disease. Statements have not been evaluated by the MHRA or the FDA.
                </span>
              </li>
            </ul>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
